import React, { useEffect, useMemo, useState } from "react";
import {
  createAdminProject,
  deleteAdminProject,
  getAdminProjects,
  removeAdminProjectImage,
  updateAdminProject,
  uploadAdminProjectImage,
} from "../../../api/projectsAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";
import { useTranslation } from "react-i18next";

const EMPTY_FORM = {
  projectKey: "1",
  language: "en",
  title: "",
  description: "",
  highlight: "",
  location: "",
  sector: "",
  isActive: true,
  images: [],
};

const mapProjectToForm = (project) => ({
  projectKey: project?.projectKey || "",
  language: String(project?.language || "en").toLowerCase().split("-")[0],
  title: project?.title || "",
  description: project?.description || "",
  highlight: project?.highlight || "",
  location: project?.location || "",
  sector: project?.sector || "",
  isActive: typeof project?.isActive === "boolean" ? project.isActive : true,
  images: Array.isArray(project?.images)
    ? project.images.map((img) => ({ id: img?.id, imageUrl: img?.imageUrl || "" }))
    : [],
});

const toPayload = (form) => ({
  projectKey: form.projectKey.trim(),
  language: String(form.language || "en").toLowerCase(),
  title: form.title.trim(),
  description: form.description.trim(),
  highlight: form.highlight.trim(),
  location: form.location.trim(),
  sector: form.sector.trim(),
  isActive: Boolean(form.isActive),
});

const ProjectsManager = () => {
  const { i18n } = useTranslation();
  const { notify } = useNotification();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [createCoverFile, setCreateCoverFile] = useState(null);
  const [createUploadFiles, setCreateUploadFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editCoverFile, setEditCoverFile] = useState(null);
  const [editUploadFiles, setEditUploadFiles] = useState([]);

  const getNextProjectKey = (list) => {
    const maxKey = (Array.isArray(list) ? list : [])
      .map((item) => Number.parseInt(String(item?.projectKey || "").trim(), 10))
      .filter((value) => Number.isFinite(value))
      .reduce((max, value) => Math.max(max, value), 0);
    return String(maxKey + 1);
  };

  const loadProjects = async () => {
    setLoading(true);
    try {
      const data = await getAdminProjects();
      const normalized = Array.isArray(data) ? data : [];
      setProjects(normalized);
      setCreateForm((prev) => ({ ...prev, projectKey: getNextProjectKey(normalized) }));
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load projects",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const sortedProjects = useMemo(() => {
    const currentLang = String(i18n.language || "en").toLowerCase().split("-")[0];
    const filtered = projects.filter(
      (item) => String(item?.language || "en").toLowerCase().split("-")[0] === currentLang
    );
    return [...filtered].sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
  }, [projects, i18n.language]);

  const handleCreate = async () => {
    if (!createForm.projectKey.trim() || !createForm.title.trim()) {
      notify({ type: "error", title: "Project key and title are required" });
      return;
    }
    setCreating(true);
    try {
      const created = await createAdminProject(toPayload(createForm));
      if (created?.id) {
        if (createCoverFile) {
          const uploadedProject = await uploadAdminProjectImage({ id: created.id, file: createCoverFile });
          const uploadedImages = Array.isArray(uploadedProject?.images) ? uploadedProject.images : [];
          const coverCandidate = uploadedImages[uploadedImages.length - 1];
          if (coverCandidate?.imageUrl) {
            await updateAdminProject({ id: created.id, payload: { coverImageUrl: coverCandidate.imageUrl } });
          }
        }
        for (const file of createUploadFiles) {
          await uploadAdminProjectImage({ id: created.id, file });
        }
      }
      notify({ type: "success", title: "Project created" });
      setCreateForm((prev) => ({ ...EMPTY_FORM, projectKey: prev.projectKey }));
      setCreateCoverFile(null);
      setCreateUploadFiles([]);
      await loadProjects();
    } catch (error) {
      notify({
        type: "error",
        title: "Create failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (project) => {
    setEditingId(project.id);
    setEditForm(mapProjectToForm(project));
    setEditCoverFile(null);
    setEditUploadFiles([]);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!editForm.projectKey.trim() || !editForm.title.trim()) {
      notify({ type: "error", title: "Project key and title are required" });
      return;
    }
    try {
      await updateAdminProject({ id: editingId, payload: toPayload(editForm) });
      if (editCoverFile) {
        const uploadedProject = await uploadAdminProjectImage({ id: editingId, file: editCoverFile });
        const uploadedImages = Array.isArray(uploadedProject?.images) ? uploadedProject.images : [];
        const coverCandidate = uploadedImages[uploadedImages.length - 1];
        if (coverCandidate?.imageUrl) {
          await updateAdminProject({ id: editingId, payload: { coverImageUrl: coverCandidate.imageUrl } });
        }
      }
      if (editUploadFiles.length) {
        for (const file of editUploadFiles) {
          await uploadAdminProjectImage({ id: editingId, file });
        }
      }
      notify({ type: "success", title: "Project updated" });
      setEditingId(null);
      setEditForm(EMPTY_FORM);
      setEditCoverFile(null);
      setEditUploadFiles([]);
      await loadProjects();
    } catch (error) {
      notify({
        type: "error",
        title: "Update failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const handleDelete = async (project) => {
    const ok = window.confirm(`Delete project "${project?.title || project?.projectKey}"?`);
    if (!ok) return;
    try {
      await deleteAdminProject({ id: project.id });
      notify({ type: "success", title: "Project deleted" });
      await loadProjects();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const removeImageFromProject = async ({ id, imageId }) => {
    try {
      await removeAdminProjectImage({ id, imageId });
      notify({ type: "success", title: "Image removed" });
      await loadProjects();
    } catch (error) {
      notify({
        type: "error",
        title: "Remove failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const renderForm = ({
    form,
    setForm,
    coverFile,
    setCoverFile,
    uploadFiles,
    setUploadFiles,
    isEdit = false,
    projectId = null,
  }) => (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="text-sm text-slate-700">
        Project Key
        <input
          type="text"
          value={form.projectKey}
          onChange={(e) => setForm((p) => ({ ...p, projectKey: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="text-sm text-slate-700">
        Language
        <select
          value={form.language}
          onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        >
          <option value="en">EN</option>
          <option value="ar">AR</option>
          <option value="ku">KU</option>
        </select>
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Title
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Description
        <textarea
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Highlight
        <textarea
          value={form.highlight}
          onChange={(e) => setForm((p) => ({ ...p, highlight: e.target.value }))}
          className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="text-sm text-slate-700">
        Location
        <input
          type="text"
          value={form.location}
          onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="text-sm text-slate-700">
        Sector
        <input
          type="text"
          value={form.sector}
          onChange={(e) => setForm((p) => ({ ...p, sector: e.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Cover image file
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
          className="mt-1 block w-full text-sm"
        />
        {coverFile?.name ? (
          <span className="mt-1 block text-xs text-slate-500">Selected cover: {coverFile.name}</span>
        ) : null}
      </label>
      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={Boolean(form.isActive)}
          onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
        />
        Active
      </label>

      {isEdit ? (
        <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Current gallery images</p>
          {form.images?.length ? (
            <div className="grid sm:grid-cols-3 gap-3">
              {form.images.map((img, idx) => {
                const imageUrl = resolveMediaUrl(img?.imageUrl);
                return (
                  <div key={`${img?.id || idx}`} className="rounded border border-slate-200 p-2">
                    {imageUrl ? (
                      <img src={imageUrl} alt="" className="w-full h-28 object-cover rounded" />
                    ) : (
                      <div className="w-full h-28 rounded bg-slate-100" />
                    )}
                    {projectId && img?.id ? (
                      <button
                        type="button"
                        onClick={() => removeImageFromProject({ id: projectId, imageId: img.id })}
                        className="mt-2 text-xs px-2 py-1 rounded border border-rose-200 text-rose-700"
                      >
                        Delete image
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-slate-500">No gallery images yet.</p>
          )}
        </div>
      ) : null}

      <label className="text-sm text-slate-700 sm:col-span-2">
        Upload extra gallery image(s)
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
          className="mt-1 block w-full text-sm"
        />
        {uploadFiles?.length ? (
          <span className="mt-1 block text-xs text-slate-500">{uploadFiles.length} file(s) selected</span>
        ) : null}
      </label>
    </div>
  );

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">Projects Manager</h2>
            <p className="text-sm text-slate-500 mt-1">CRUD projects and manage project gallery images.</p>
          </div>
          <button
            type="button"
            onClick={loadProjects}
            className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm"
          >
            Reload
          </button>
        </div>

        {renderForm({
          form: createForm,
          setForm: setCreateForm,
          coverFile: createCoverFile,
          setCoverFile: setCreateCoverFile,
          uploadFiles: createUploadFiles,
          setUploadFiles: setCreateUploadFiles,
        })}

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={creating}
            onClick={handleCreate}
            className="px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 text-sm disabled:opacity-60"
          >
            {creating ? "Creating..." : "Create project"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Key</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Lang</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Title</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Cover</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">
                  Loading projects...
                </td>
              </tr>
            ) : sortedProjects.length ? (
              sortedProjects.map((item) => {
                const cover = resolveMediaUrl(item?.coverImageUrl);
                return (
                  <tr key={item.id} className="border-b border-slate-200 align-top">
                    <td className="px-3 py-3 text-sm text-slate-700">{item.id}</td>
                    <td className="px-3 py-3 text-sm text-slate-700">{item.projectKey || "-"}</td>
                    <td className="px-3 py-3 text-sm text-slate-700 uppercase">{item.language || "-"}</td>
                    <td className="px-3 py-3 text-sm font-medium text-slate-900">{item.title || "-"}</td>
                    <td className="px-3 py-3">
                      {cover ? (
                        <img src={cover} alt="" className="w-24 h-16 object-cover rounded border border-slate-200" />
                      ) : (
                        <p className="text-xs text-slate-500">No cover</p>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => startEdit(item)}
                          className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">
                  No projects found for this language.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold text-[#011337]">Edit Project #{editingId}</h3>
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setEditForm(EMPTY_FORM);
                setEditCoverFile(null);
                setEditUploadFiles([]);
              }}
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          {renderForm({
            form: editForm,
            setForm: setEditForm,
            coverFile: editCoverFile,
            setCoverFile: setEditCoverFile,
            uploadFiles: editUploadFiles,
            setUploadFiles: setEditUploadFiles,
            isEdit: true,
            projectId: editingId,
          })}

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={saveEdit}
              className="px-4 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90 text-sm"
            >
              Save changes
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ProjectsManager;
